// FILE: src/pages/admin/AdminAccessLinksPage.jsx
// Reviewer access links (plan WP-2b, matrix 10.5). Admins make a private,
// one-time link per kind of account for the MTO reviewers; the link is shown
// once, with a ready-to-paste email. Links never expire, work once, and can
// be revoked while unused. The list never shows the links themselves.
import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import AddLinkIcon from "@mui/icons-material/AddLink";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import { EmptyState, ErrorState, LoadingState, PageHeader, StatusChip } from "../../components/ui";
import { errorMessage } from "../../i18n/errors";
import {
  KIND_OPTIONS,
  createAccessLink,
  listAccessLinks,
  revokeAccessLink,
  reviewerEmailText
} from "../../services/accessLinks";

const STATUS = {
  ACTIVE: { label: "Not used yet", tone: "info", icon: <AddLinkIcon /> },
  USED: { label: "Used", tone: "success", icon: <HowToRegOutlinedIcon /> },
  REVOKED: { label: "Revoked", tone: "default", icon: <LinkOffIcon /> }
};

const fmt = (d) =>
  d ? new Date(d).toLocaleString("en-CA", { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—";

/** Read-only text with a Copy button that says when it worked. */
export function CopyField({ label, value, multiline = false, testId }) {
  const [copied, setCopied] = useState(null); // null | "ok" | "failed"
  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied("ok");
    } catch {
      setCopied("failed");
    }
  }
  return (
    <Box>
      <TextField
        label={label}
        value={value}
        fullWidth
        multiline={multiline}
        minRows={multiline ? 8 : undefined}
        slotProps={{ input: { readOnly: true }, htmlInput: { "data-testid": testId } }}
        onFocus={(e) => e.target.select()}
      />
      <Stack direction="row" spacing={1.5} sx={{ mt: 1, alignItems: "center" }}>
        <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={copy} aria-label={`Copy ${label.toLowerCase()}`}>
          Copy
        </Button>
        <Typography role="status" aria-live="polite" sx={{ color: copied === "failed" ? "error.main" : "text.secondary" }}>
          {copied === "ok" ? "Copied." : copied === "failed" ? "Couldn't copy: select the text and copy it yourself." : ""}
        </Typography>
      </Stack>
    </Box>
  );
}

function CreateLinkDialog({ open, onClose, onCreated }) {
  const [kind, setKind] = useState("STUDENT_TIMED");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setError(null);
      setLabel("");
    }
  }, [open]);

  async function submit(e) {
    e.preventDefault();
    setError(null);
    if (!label.trim()) {
      setError({ code: "MISSING_REQUIRED_FIELDS" });
      return;
    }
    setBusy(true);
    try {
      onCreated(await createAccessLink(kind, label.trim()));
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} aria-labelledby="create-link-title" fullWidth maxWidth="sm">
      <Box component="form" onSubmit={submit} noValidate>
        <DialogTitle id="create-link-title">New access link</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} role="alert">
              {errorMessage(error)}
            </Alert>
          )}
          <FormControl component="fieldset" sx={{ width: "100%", mb: 2.5 }}>
            <FormLabel component="legend" sx={{ fontWeight: 650, color: "text.primary", mb: 1 }}>
              Kind of account
            </FormLabel>
            <RadioGroup value={kind} onChange={(e) => setKind(e.target.value)} sx={{ gap: 1 }}>
              {KIND_OPTIONS.map((o) => (
                <FormControlLabel
                  key={o.value}
                  value={o.value}
                  control={<Radio />}
                  sx={{ alignItems: "flex-start", m: 0, p: 1.5, borderRadius: 2, border: "1px solid", borderColor: kind === o.value ? "primary.main" : "divider" }}
                  label={
                    <Box sx={{ pt: 0.75 }}>
                      <Typography sx={{ fontWeight: 650 }}>{o.label}</Typography>
                      <Typography sx={{ color: "text.secondary" }}>{o.hint}</Typography>
                    </Box>
                  }
                />
              ))}
            </RadioGroup>
          </FormControl>
          <TextField
            label="Who is it for?"
            required
            fullWidth
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            inputProps={{ maxLength: 120 }}
            helperText="For example: MTO reviewer – student (timed). The reviewer sees this when they open the link."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={busy}>
            Create link
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

function RevokeDialog({ link, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  async function revoke() {
    setBusy(true);
    setError(null);
    try {
      await revokeAccessLink(link.id);
      onDone();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={!!link} onClose={onClose} aria-labelledby="revoke-title" aria-describedby="revoke-desc">
      <DialogTitle id="revoke-title">Revoke this link?</DialogTitle>
      <DialogContent>
        <DialogContentText id="revoke-desc">
          “{link?.label}” will stop working at once. Anyone who opens it will be told the link isn't valid. This can't be
          undone; you can make a new link instead.
        </DialogContentText>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }} role="alert">
            {errorMessage(error)}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy} autoFocus>
          Keep it
        </Button>
        <Button color="error" variant="contained" onClick={revoke} disabled={busy}>
          Revoke link
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function AdminAccessLinksPage() {
  const [state, setState] = useState({ links: null, error: null });
  const [creating, setCreating] = useState(false);
  const [fresh, setFresh] = useState([]); // links made on this page, with their URLs
  const [revoking, setRevoking] = useState(null);

  const load = useCallback(() => {
    listAccessLinks()
      .then((data) => setState({ links: data.links || [], error: null }))
      .catch((error) => setState({ links: null, error }));
  }, []);
  useEffect(() => load(), [load]);

  const created = (link) => {
    setCreating(false);
    setFresh((list) => [link, ...list]);
    load();
  };

  return (
    <Box>
      <PageHeader
        title="Reviewer access links"
        description="Private, one-time links for MTO reviewers. Each makes one account of the kind you choose, with the reviewer's own email address. Links never expire and can be revoked while unused."
        actions={
          <Button variant="contained" startIcon={<AddLinkIcon />} onClick={() => setCreating(true)}>
            New access link
          </Button>
        }
      />

      {fresh.length > 0 && (
        <Card sx={{ mb: 3, borderColor: "primary.main" }} data-testid="fresh-links">
          <CardContent>
            <Alert severity="success" icon={<CheckCircleOutlineIcon />} sx={{ mb: 2 }}>
              <AlertTitle>Copy {fresh.length === 1 ? "this link" : "these links"} now</AlertTitle>
              For security, a link is shown only once. If you lose it, revoke it and make a new one.
            </Alert>
            <Stack spacing={2.5}>
              {fresh.map((l) => (
                <CopyField key={l.id} label={`${l.kindLabel} link`} value={l.url} testId={`link-url-${l.kind}`} />
              ))}
              <CopyField label="Email text" value={reviewerEmailText(fresh)} multiline testId="link-email-text" />
              <Box>
                <Button onClick={() => setFresh([])}>I've copied them, hide</Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      {state.error ? (
        <ErrorState error={state.error} onRetry={load} />
      ) : state.links === null ? (
        <LoadingState variant="table" rows={3} label="Loading access links…" />
      ) : state.links.length === 0 ? (
        <EmptyState
          title="No access links yet"
          description="Make one link for each reviewer account: Student – timed, Student – untimed and Administrative access."
          action={
            <Button variant="contained" onClick={() => setCreating(true)}>
              New access link
            </Button>
          }
        />
      ) : (
        <TableContainer component={Card} sx={{ overflowX: "auto" }}>
          <Table aria-label="Access links">
            <TableHead>
              <TableRow>
                <TableCell>For</TableCell>
                <TableCell>Kind</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Used by</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {state.links.map((l) => {
                const s = STATUS[l.status] || STATUS.ACTIVE;
                return (
                  <TableRow key={l.id} hover>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
                      {l.label}
                    </TableCell>
                    <TableCell>{l.kindLabel}</TableCell>
                    <TableCell>
                      <StatusChip label={s.label} tone={s.tone} icon={s.icon} />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {fmt(l.createdAt)}
                      {l.createdBy?.email && <Typography sx={{ color: "text.secondary" }}>{l.createdBy.email}</Typography>}
                    </TableCell>
                    <TableCell>
                      {l.status === "USED" ? (
                        <>
                          <Typography>{l.usedBy?.name || l.usedBy?.email || "—"}</Typography>
                          <Typography sx={{ color: "text.secondary" }}>
                            {l.usedBy?.name ? `${l.usedBy.email}, ` : ""}
                            {fmt(l.usedAt)}
                          </Typography>
                        </>
                      ) : l.status === "REVOKED" ? (
                        <Typography sx={{ color: "text.secondary" }}>Revoked {fmt(l.revokedAt)}</Typography>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {l.status === "ACTIVE" && (
                        <Button color="error" onClick={() => setRevoking(l)} aria-label={`Revoke the link for ${l.label}`}>
                          Revoke
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <CreateLinkDialog open={creating} onClose={() => setCreating(false)} onCreated={created} />
      <RevokeDialog
        link={revoking}
        onClose={() => setRevoking(null)}
        onDone={() => {
          setRevoking(null);
          load();
        }}
      />
    </Box>
  );
}
